import { CloseOutlined } from '@ant-design/icons';
import { ButtonProps, Modal as Modalantd } from 'antd';
import cx from 'classnames';
import React, { CSSProperties, FunctionComponent, ReactNode, useEffect, useState } from 'react';
import styles from './modal.module.scss';

type Props = {
	children: React.ReactNode;
	visible: boolean;
	onClickOk?: () => void;
	onCancel?: () => void;
	title?: string | ReactNode;
	className?: string;
	footer?: React.ReactNode[];
	width?: string | number;
	style?: CSSProperties;
	customZoomClass?: string;
	keyboard?: boolean;
	maskClosable?: boolean;
	destroyOnClose?: boolean;
	bodyStyle?: React.CSSProperties;
	closable?: boolean;
	maskStyle?: React.CSSProperties;
	closeIconId?: string;
	nestedModal?: boolean;
	okButtonProps?: ButtonProps;
	closeIconStyle?: CSSProperties;
	hideCloseButton?: boolean;
	zIndex?: number;
	addHelpButton?: React.ReactNode;
};

const Modal: FunctionComponent<Props> = ({
	visible,
	onClickOk,
	children,
	title,
	className,
	onCancel,
	footer,
	width,
	style,
	customZoomClass,
	keyboard = false,
	maskClosable = false,
	zIndex = 1000,
	destroyOnClose,
	bodyStyle,
	closable,
	maskStyle,
	closeIconId = 'closeModalIcon',
	nestedModal,
	okButtonProps,
	closeIconStyle,
	hideCloseButton,
	addHelpButton,
}) => {
	// #region state management variables
	const [helpHovered, setHelpHovered] = useState(false);
	// #endregion

	///remove scroll when modal is opened
	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			if (!nestedModal) {
				document.body.style.overflow = 'auto';
			}
		};
	}, []);

	const defaultCloseIcon = <CloseOutlined id={closeIconId} style={{ fontSize: '12px', ...closeIconStyle }} />;

	const extraContent = addHelpButton ? (
		<div
			className={styles.extraContent}
			onMouseEnter={e => {
				e.stopPropagation();
				setHelpHovered(true);
			}}
			onMouseLeave={e => {
				e.stopPropagation();
				setHelpHovered(false);
			}}
			onClick={e => {
				e.stopPropagation();
				e.preventDefault();
			}}>
			{addHelpButton}
		</div>
	) : null;

	const closeIconElement = extraContent ? (
		<div className={styles.closeIconWrapper}>
			{extraContent}
			<div className={styles.closeIcon}>{defaultCloseIcon}</div>
		</div>
	) : (
		defaultCloseIcon
	);

	return (
		<Modalantd
			zIndex={zIndex}
			keyboard={keyboard}
			closable={closable}
			destroyOnClose={destroyOnClose}
			maskClosable={maskClosable}
			open={visible}
			className={className}
			title={title}
			wrapClassName={cx(customZoomClass || styles.WithModalZoom, { noHover: helpHovered })}
			onOk={onClickOk}
			onCancel={onCancel}
			centered
			width={width}
			styles={{ body: bodyStyle, mask: maskStyle }}
			style={style}
			footer={footer ? [...footer] : null}
			closeIcon={hideCloseButton ? null : closeIconElement}
			okButtonProps={okButtonProps}>
			{children}
		</Modalantd>
	);
};

export { Modal };
